import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { util as sdkUtil } from '../util/sdkLoader';
import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import { createSlug } from '../util/urlHelpers';
import * as log from '../util/log';
import { LISTING_STATE_DRAFT } from '../util/types';
import { storableError } from '../util/errors';
import { isUserAuthorized, getCurrentUserTypeRoles } from '../util/userHelpers';
import defaultConfig from '../config/configDefault';
import { mergeConfig } from '../util/configHelpers';
import {
  getStatesNeedingProviderAttention,
  getStatesNeedingCustomerAttention,
} from '../transactions/transaction';

import { authInfo } from './auth.duck';
import { updateStripeConnectAccount } from './stripeConnectAccount.duck';

// ================ Helper Functions ================ //

/** Rebuild hosted config object from Redux (same shape as index.js before mergeConfig). */
const hostedConfigFromAppAssets = state => {
  const appAssets = state?.hostedAssets?.appAssets;
  if (!appAssets || typeof appAssets !== 'object') {
    return {};
  }
  return Object.entries(appAssets).reduce((acc, [name, content]) => {
    if (name === 'translations' || name === 'footer') {
      return acc;
    }
    return { ...acc, [name]: content?.data || {} };
  }, {});
};

const mergeAppConfigFromState = getState => {
  return mergeConfig(hostedConfigFromAppAssets(getState()), defaultConfig);
};

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
    ? newCurrentUser
    : { id, type, attributes, ...oldRelationships, ...relationships };
};

// ================ Async Thunks ================ //

//////////////////////////////////////////////////////////////////////
// Fetch ownListings to check if currentUser has published listings //
//////////////////////////////////////////////////////////////////////

const fetchCurrentUserHasListingsPayloadCreator = (_, thunkAPI) => {
  const { getState, extra: sdk, rejectWithValue } = thunkAPI;
  const { currentUser } = getState().user;

  if (!currentUser) {
    return Promise.resolve({ hasListings: false });
  }

  const params = {
    // Since we are only interested in if the user has published
    // listings, we only need at most one result.
    states: 'published',
    page: 1,
    perPage: 1,
  };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      return { hasListings: !!hasPublishedListings };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasListingsThunk = createAsyncThunk(
  'user/fetchCurrentUserHasListings',
  fetchCurrentUserHasListingsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserHasListingsThunk()).unwrap();
};

//////////////////////////////////////////////////////////////////////
// Fetch first own listing (id, slug) for profile/edit links        //
//////////////////////////////////////////////////////////////////////

const fetchCurrentUserFirstListingPayloadCreator = (_, { extra: sdk, rejectWithValue }) => {
  return sdk.ownListings
    .query({ perPage: 1 })
    .then(response => {
      const data = response?.data?.data;
      const listing = Array.isArray(data) && data.length > 0 ? data[0] : null;
      if (!listing) return { id: null, slug: null };
      const id = listing.id?.uuid ?? listing.id;
      const title = listing.attributes?.title ?? '';
      const slug = createSlug(title);
      return { id, slug };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserFirstListingThunk = createAsyncThunk(
  'user/fetchCurrentUserFirstListing',
  fetchCurrentUserFirstListingPayloadCreator
);

export const fetchCurrentUserFirstListing = () => (dispatch, getState, sdk) =>
  dispatch(fetchCurrentUserFirstListingThunk(undefined, { extra: sdk })).unwrap();

///////////////////////////////////////////////////////////
// Fetch transactions to check if currentUser has orders //
///////////////////////////////////////////////////////////

const fetchCurrentUserHasOrdersPayloadCreator = (_, { getState, extra: sdk, rejectWithValue }) => {
  if (!getState().user.currentUser) {
    return Promise.resolve({ hasOrders: false });
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      return { hasOrders: !!hasOrders };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasOrdersThunk = createAsyncThunk(
  'user/fetchCurrentUserHasOrders',
  fetchCurrentUserHasOrdersPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserHasOrdersThunk()).unwrap();
};

/////////////////////////////////////////////////////////////////////////////////////
// Fetch transactions in specific states to check if currentUser has notifications //
/////////////////////////////////////////////////////////////////////////////////////

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

const fetchCurrentUserNotificationsPayloadCreator = (_, { extra: sdk, rejectWithValue, getState }) => {
  const statesNeedingProviderAttention = getStatesNeedingProviderAttention() || [];
  const statesNeedingCustomerAttention = getStatesNeedingCustomerAttention() || [];

  const paramsForSales = {
    only: 'sale',
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };
  const paramsForOrders = {
    only: 'order',
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  const stateName = txState => (typeof txState === 'string' ? txState.replace(/^state\//, '') : null);
  const shouldCountSaleNotification = tx => {
    const state = stateName(tx?.attributes?.state);
    const needsProviderAttention = !!state && statesNeedingProviderAttention.includes(state);
    return needsProviderAttention;
  };
  const shouldCountOrderNotification = tx => {
    const state = stateName(tx?.attributes?.state);
    const needsCustomerAttention = !!state && statesNeedingCustomerAttention.includes(state);
    return needsCustomerAttention;
  };
  const shouldCountSaleMessage = tx => {
    const hasUnreadMessageForProvider = tx?.attributes?.metadata?.lastMessageSeenByProvider === false;
    return hasUnreadMessageForProvider;
  };
  const shouldCountOrderMessage = tx => {
    const hasUnreadMessageForCustomer = tx?.attributes?.metadata?.lastMessageSeenByCustomer === false;
    return hasUnreadMessageForCustomer;
  };

  const currentUser = getState()?.user?.currentUser;
  const config = mergeAppConfigFromState(getState);
  const roles = getCurrentUserTypeRoles(config, currentUser);
  const querySales = roles.provider === true;
  const queryOrders = roles.customer === true;

  const emptyCounts = () => ({
    saleNotificationsCount: 0,
    orderNotificationsCount: 0,
    saleMessagesCount: 0,
    orderMessagesCount: 0,
  });

  const countFromSales = sales => {
    const salesData = sales?.data?.data || [];
    return {
      saleNotificationsCount: salesData.filter(shouldCountSaleNotification).length,
      orderNotificationsCount: 0,
      saleMessagesCount: salesData.filter(shouldCountSaleMessage).length,
      orderMessagesCount: 0,
    };
  };

  const countFromOrders = orders => {
    const ordersData = orders?.data?.data || [];
    return {
      saleNotificationsCount: 0,
      orderNotificationsCount: ordersData.filter(shouldCountOrderNotification).length,
      saleMessagesCount: 0,
      orderMessagesCount: ordersData.filter(shouldCountOrderMessage).length,
    };
  };

  const mergeCounts = (a, b) => ({
    saleNotificationsCount: a.saleNotificationsCount + b.saleNotificationsCount,
    orderNotificationsCount: a.orderNotificationsCount + b.orderNotificationsCount,
    saleMessagesCount: a.saleMessagesCount + b.saleMessagesCount,
    orderMessagesCount: a.orderMessagesCount + b.orderMessagesCount,
  });

  if (querySales && queryOrders) {
    return Promise.all([
      sdk.transactions.query(paramsForSales),
      sdk.transactions.query(paramsForOrders),
    ])
      .then(([sales, orders]) => mergeCounts(countFromSales(sales), countFromOrders(orders)))
      .catch(e => rejectWithValue(storableError(e)));
  }

  if (querySales) {
    return sdk.transactions
      .query(paramsForSales)
      .then(countFromSales)
      .catch(e => rejectWithValue(storableError(e)));
  }

  if (queryOrders) {
    return sdk.transactions
      .query(paramsForOrders)
      .then(countFromOrders)
      .catch(e => rejectWithValue(storableError(e)));
  }

  return Promise.resolve(emptyCounts());
};

export const fetchCurrentUserNotificationsThunk = createAsyncThunk(
  'user/fetchCurrentUserNotifications',
  fetchCurrentUserNotificationsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserNotifications = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserNotificationsThunk()).unwrap();
};

const fetchCurrentUserPayloadCreator = (options, thunkAPI) => {
  const { getState, dispatch, extra: sdk, rejectWithValue } = thunkAPI;
  const state = getState();
  const { currentUserHasListings, currentUserShowTimestamp } = state.user || {};
  const { isAuthenticated } = state.auth;
  const {
    callParams = null,
    updateHasListings = true,
    updateNotifications = true,
    afterLogin,
    enforce = false, // Automatic emailVerification might be called too fast
  } = options || {};

  // Double fetch might happen when e.g. profile page is making a full page load
  const aSecondAgo = new Date().getTime() - 1000;
  if (!enforce && currentUserShowTimestamp > aSecondAgo) {
    return Promise.resolve(state.user.currentUser);
  }

  if (!isAuthenticated && !afterLogin) {
    // Make sure current user is null
    return Promise.resolve(null);
  }

  const parameters = callParams || {
    include: ['effectivePermissionSet', 'profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(updateStripeConnectAccount(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      return currentUser;
    })
    .then(currentUser => {
      // If currentUser is not active (e.g. in 'pending-approval' state),
      // then they don't have listings or transactions that we care about.
      if (isUserAuthorized(currentUser)) {
        if (currentUserHasListings === false && updateHasListings !== false) {
          dispatch(fetchCurrentUserHasListings());
        }

        if (updateHasListings !== false) {
          dispatch(fetchCurrentUserFirstListingThunk(undefined, { extra: sdk }));
        }

        if (updateNotifications !== false) {
          dispatch(fetchCurrentUserNotifications());
        }

        if (!currentUser.attributes.emailVerified) {
          dispatch(fetchCurrentUserHasOrders());
        }
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
      return currentUser;
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      return rejectWithValue(storableError(e));
    });
};

export const fetchCurrentUserThunk = createAsyncThunk(
  'user/fetchCurrentUser',
  fetchCurrentUserPayloadCreator
);
// Backward compatible wrapper for the thunk
/**
 * Fetch currentUser API entity.
 *
 * @param {Object} options
 * @param {Object} [options.callParams]           Optional parameters for the currentUser.show().
 * @param {boolean} [options.updateHasListings]   Make extra call for fetchCurrentUserHasListings()?
 * @param {boolean} [options.updateNotifications] Make extra call for fetchCurrentUserNotifications()?
 * @param {boolean} [options.afterLogin]          Fetch is no-op for unauthenticated users except after login() call
 * @param {boolean} [options.enforce]             Enforce the call even if the currentUser entity is freshly fetched.
 */
export const fetchCurrentUser = options => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserThunk(options)).unwrap();
};

/////////////////////////////////////////////
// Send verification email to currentUser //
/////////////////////////////////////////////

const sendVerificationEmailPayloadCreator = (_, { extra: sdk, rejectWithValue }) => {
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => ({}))
    .catch(e => rejectWithValue(storableError(e)));
};
export const sendVerificationEmailThunk = createAsyncThunk(
  'user/sendVerificationEmail',
  sendVerificationEmailPayloadCreator,
  {
    condition: (_, { getState }) => {
      return !getState()?.user?.sendVerificationEmailInProgress;
    },
  }
);

// Backward compatible wrapper for the thunk
export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  return dispatch(sendVerificationEmailThunk()).unwrap();
};

// ================ Slice ================ //

const userSlice = createSlice({
  name: 'user',
  initialState: {
    currentUser: null,
    currentUserShowTimestamp: 0,
    currentUserShowError: null,
    currentUserHasListings: false,
    currentUserHasListingsError: null,
    currentUserFirstListing: null, // { id, slug } or null, for profile/edit listing links
    currentUserSaleNotificationCount: 0,
    currentUserOrderNotificationCount: 0,
    currentUserSaleMessageCount: 0,
    currentUserOrderMessageCount: 0,
    currentUserNotificationCountError: null,
    currentUserHasOrders: null, // This is not fetched unless unverified emails exist
    currentUserHasOrdersError: null,
    sendVerificationEmailInProgress: false,
    sendVerificationEmailError: null,
  },
  reducers: {
    clearCurrentUser: state => {
      state.currentUser = null;
      state.currentUserShowError = null;
      state.currentUserHasListings = false;
      state.currentUserHasListingsError = null;
      state.currentUserFirstListing = null;
      state.currentUserSaleNotificationCount = 0;
      state.currentUserOrderNotificationCount = 0;
      state.currentUserSaleMessageCount = 0;
      state.currentUserOrderMessageCount = 0;

      state.currentUserNotificationCountError = null;
    },
    setCurrentUser: (state, action) => {
      state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
    },
    setCurrentUserHasOrders: state => {
      state.currentUserHasOrders = true;
    },
  },
  extraReducers: builder => {
    builder
      // fetchCurrentUser
      .addCase(fetchCurrentUserThunk.pending, state => {
        state.currentUserShowError = null;
      })
      .addCase(fetchCurrentUserThunk.fulfilled, (state, action) => {
        state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
        state.currentUserShowTimestamp = action.payload ? new Date().getTime() : 0;
      })
      .addCase(fetchCurrentUserThunk.rejected, (state, action) => {
        // eslint-disable-next-line no-console
        console.error(action.payload);
        state.currentUserShowError = action.payload;
      })
      // fetchCurrentUserHasListings
      .addCase(fetchCurrentUserHasListingsThunk.pending, state => {
        state.currentUserHasListingsError = null;
      })
      .addCase(fetchCurrentUserHasListingsThunk.fulfilled, (state, action) => {
        state.currentUserHasListings = action.payload.hasListings;
      })
      .addCase(fetchCurrentUserHasListingsThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserHasListingsError = action.payload;
      })
      // fetchCurrentUserFirstListing
      .addCase(fetchCurrentUserFirstListingThunk.fulfilled, (state, action) => {
        state.currentUserFirstListing =
          action.payload?.id && action.payload?.slug ? action.payload : null;
      })
      // fetchCurrentUserNotifications
      .addCase(fetchCurrentUserNotificationsThunk.pending, state => {
        state.currentUserNotificationCountError = null;
      })
      .addCase(fetchCurrentUserNotificationsThunk.fulfilled, (state, action) => {
        state.currentUserSaleNotificationCount = action.payload.saleNotificationsCount;
        state.currentUserOrderNotificationCount = action.payload.orderNotificationsCount;
        state.currentUserSaleMessageCount = action.payload.saleMessagesCount;
        state.currentUserOrderMessageCount = action.payload.orderMessagesCount;
      })
      .addCase(fetchCurrentUserNotificationsThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserNotificationCountError = action.payload;
      })
      // fetchCurrentUserHasOrders
      .addCase(fetchCurrentUserHasOrdersThunk.pending, state => {
        state.currentUserHasOrdersError = null;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.fulfilled, (state, action) => {
        state.currentUserHasOrders = action.payload.hasOrders;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserHasOrdersError = action.payload;
      })
      // sendVerificationEmail
      .addCase(sendVerificationEmailThunk.pending, state => {
        state.sendVerificationEmailInProgress = true;
        state.sendVerificationEmailError = null;
      })
      .addCase(sendVerificationEmailThunk.fulfilled, state => {
        state.sendVerificationEmailInProgress = false;
      })
      .addCase(sendVerificationEmailThunk.rejected, (state, action) => {
        state.sendVerificationEmailInProgress = false;
        state.sendVerificationEmailError = action.payload;
      });
  },
});

export default userSlice.reducer;

export const { clearCurrentUser, setCurrentUser, setCurrentUserHasOrders } = userSlice.actions;

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};

export const getCurrentUserFirstListing = state => state.user?.currentUserFirstListing ?? null;
